import { useEffect, useState, useCallback } from 'react';
import type { ApiResponse } from '../services/http/types';
import { listCustomers, createCustomer, updateCustomer, deleteCustomer } from '../services/customer/customer.service';
import type { CreateCustomerRequest, UpdateCustomerRequest, Customer } from '../services/customer/customer.types';

type CustomerLike = Partial<Customer> & {
  id?: number | string;
  name?: string;
};

const getErrorMessage = (error: unknown): string => {
  return error instanceof Error ? error.message : String(error);
};

// API sometimes returns id as string; normalize to our internal Customer type which expects id:number
const normalizeCustomer = (raw: CustomerLike): Customer => {
  const idNum = Number(raw.id);
  return {
    id: Number.isFinite(idNum) ? idNum : 0,
    customerName: raw.customerName ?? raw.name ?? String(raw.id ?? ''),
    address1: raw.address1 ?? null,
    address2: raw.address2 ?? null,
    townCity: raw.townCity ?? null,
    county: raw.county ?? null,
    country: raw.country ?? null,
    postCode: raw.postCode ?? null,
    isDeleted: raw.isDeleted ?? false,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
    createdBy: raw.createdBy ?? null,
    updatedBy: raw.updatedBy ?? null,
  };
};

export default function useCustomers() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listCustomers();
      if (res?.success && res.data) {
        setCustomers(res.data.map(normalizeCustomer));
      }
      else setError(res?.error?.message || 'Failed to load customers');
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (payload: CreateCustomerRequest) => {
    setError(null);
    try {
      const res = await createCustomer(payload);
      if (res?.success && res.data) {
        setCustomers(prev => [normalizeCustomer(res.data), ...prev]);
      }
      return res;
    } catch (err: unknown) {
      const message = getErrorMessage(err);
      setError(message);
      return { success: false, data: null, error: { message } } as ApiResponse<Customer>;
    }
  };

  const update = async (id: string | number, payload: UpdateCustomerRequest) => {
    setError(null);
    setActionLoading(prev => ({ ...prev, [String(id)]: true }));
    try {
      const res = await updateCustomer(id, payload);
      if (res?.success && res.data) {
        const updated = normalizeCustomer(res.data);
        setCustomers(prev => prev.map(c => (c.id === updated.id ? updated : c)));
      }
      return res;
    } catch (err: unknown) {
      const message = getErrorMessage(err);
      setError(message);
      return { success: false, data: null, error: { message } } as ApiResponse<Customer>;
    } finally {
      setActionLoading(prev => ({ ...prev, [String(id)]: false }));
    }
  };

  const remove = async (id: string | number) => {
    setError(null);
    setActionLoading(prev => ({ ...prev, [String(id)]: true }));
    try {
      const res = await deleteCustomer(id);
      if (res?.success) {
        setCustomers(prev => prev.filter(c => c.id !== Number(id)));
      }
      return res;
    } catch (err: unknown) {
      const message = getErrorMessage(err);
      setError(message);
      return { success: false, data: null, error: { message } } as ApiResponse<null>;
    } finally {
      setActionLoading(prev => ({ ...prev, [String(id)]: false }));
    }
  };

  return {
    customers,
    loading,
    error,
    actionLoading,
    reload: load,
    create,
    update,
    remove,
    setCustomers,
  };
}

// also expose a named export so callers using `{ useCustomers }` work
export { useCustomers };
